import {MicroserviceResponse} from '@digital-advice/ms-core-sdk';
import {
  // requests
  InvestmentServiceOptions,
  GetAssetClassHierarchyOptions,
  GetTickerClassificationsOptions,
  GetAssetClassesOptions,
  AssetClassByIdOptions,
  PostAssetClassesOptions,
  GetHoldingClassificationsOptions,
  PatchHoldingClassificationsOptions,
  PostTickerClassificationsOptions,
  PostAssetClassHierarchyOptions,
  GetIraConstraintsOptions,
  // responses
  InvestmentService,
  AssetClassHierarchy,
  TickerClassification,
  AssetClass,
  HoldingClass,
  IraConstraints,
} from '@digital-advice/ms-investment-sdk';
import {apiKey, getLogger, resolveEndpointForServiceToken, interceptors, sourceInfo} from '@advisor/services/sdk';

const svcOptions = {hasServiceTokenEndpoint: false};

export const defaultOptions: InvestmentServiceOptions = {
  logger: getLogger(),
  apiKey,
  endpoint: resolveEndpointForServiceToken(svcOptions.hasServiceTokenEndpoint),
  interceptors,
  sourceInfo,
};

export default class InvestmentServices {
  static async sdkService(options: InvestmentServiceOptions = defaultOptions): Promise<InvestmentService> {
    const sdkService = await new InvestmentService(options);

    return sdkService;
  }

  /**
   * Gateway endpoint: getAssetClassHierarchy
   * @param A promise is list of {@link AssetClassHierarchy} object.
   */

  static async getAssetClassHierarchy(
    payload: GetAssetClassHierarchyOptions
  ): Promise<MicroserviceResponse<AssetClassHierarchy[]>> {
    const service = await this.sdkService(defaultOptions);

    return service.getAssetClassHierarchy(payload);
  }

  /**
   * Gateway endpoint: postAssetClassHierarchy
   * @param options - A {@link PostAssetClassHierarchyOptions} object.
   * @returns void.
   */

  static async postAssetClassHierarchy(payload: PostAssetClassHierarchyOptions): Promise<void> {
    const service = await this.sdkService(defaultOptions);

    return service.postAssetClassHierarchy(payload);
  }

  static async getTickerClassifications(
    payload: GetTickerClassificationsOptions
  ): Promise<MicroserviceResponse<TickerClassification[]>> {
    const service = await this.sdkService(defaultOptions);

    return service.getTickerClassifications(payload);
  }

  static async getAssetClasses(payload: GetAssetClassesOptions): Promise<MicroserviceResponse<AssetClass[]>> {
    const service = await this.sdkService(defaultOptions);

    return service.getAssetClasses(payload);
  }

  static async createAssetClasses(payload: PostAssetClassesOptions): Promise<MicroserviceResponse<AssetClass>> {
    const service = await this.sdkService(defaultOptions);

    return service.createAssetClasses(payload);
  }

  static async updateAssetClasses(payload: AssetClassByIdOptions): Promise<MicroserviceResponse<AssetClass>> {
    const service = await this.sdkService(defaultOptions);

    return service.updateAssetClasses(payload);
  }

  static async deleteAssetClasses(payload: AssetClassByIdOptions): Promise<MicroserviceResponse<void>> {
    const service = await this.sdkService(defaultOptions);

    return service.deleteAssetClasses(payload);
  }

  static async getHoldingClassifications(
    payload: GetHoldingClassificationsOptions
  ): Promise<MicroserviceResponse<HoldingClass[]>> {
    const service = await this.sdkService(defaultOptions);

    return service.getHoldingClassifications(payload);
  }

  static async patchHoldingClassifications(
    payload: PatchHoldingClassificationsOptions
  ): Promise<MicroserviceResponse<void>> {
    const service = await this.sdkService(defaultOptions);

    return service.patchHoldingClassifications(payload);
  }

  static async insertUpdateTickerClassifications(
    payload: PostTickerClassificationsOptions
  ): Promise<MicroserviceResponse<void>> {
    const service = await this.sdkService(defaultOptions);

    return service.insertUpdateTickerClassifications(payload);
  }

  /*
   * Get IRA account constraints for Deposit
   * @param options - A {@link GetIraConstraintsOptions} object.
   * @returns A promise of a {@link AccountTypeContributionLimit} object.
   */
  static async getIraConstraintsINCOMING(options: GetIraConstraintsOptions): Promise<IraConstraints> {
    const custodianService = await this.sdkService();

    return custodianService.getIraConstraintsINCOMING(options);
  }

  /*
   * Get IRA account constraints for Withdrawal
   * @param options - A {@link GetIraConstraintsOptions} object.
   * @returns A promise of a {@link AccountTypeContributionLimit} object.
   */
  static async getIraConstraintsOUTGOING(options: GetIraConstraintsOptions): Promise<IraConstraints> {
    const custodianService = await this.sdkService();

    return custodianService.getIraConstraintsOUTGOING(options);
  }
}
